/**
 * Region-aware age-of-consent gate.
 *
 * UK pathway (DPA 2018 §9 + UK-GDPR Art 8 + ICO Children's Code):
 *   <13       — Verifiable Parental Consent required (KBA / ID+face)
 *   13–17     — child consents themselves; parent informed
 *   ≥18       — standard adult registration
 *
 * US pathway (COPPA + COPPA 2.0):
 *   <13       — Verifiable Parental Consent required
 *   13–16     — heightened caution (no profiling, no third-party ad data)
 *   ≥17       — standard
 *
 * V1 LAUNCH POSTURE (2026-05-13, see docs/spec/22a-v1-launch-posture.md):
 *   While `features.under13PlayerAccounts === false` (v1 default), the
 *   gate returns `UNDER_13_DEFERRED` for any DoB < 13 instead of the
 *   `REQUIRES_*` parental-consent statuses. No VPC / Yoti flow runs.
 *   The 13–17 + adult branches are unchanged.
 *
 * The function is the single source of truth for age gating. UI
 * routes hang off the `redirect` field. Each return shape is
 * exhaustive on `region` so callers can type-narrow with a switch.
 *
 * Wave 1 of the UK launch programme (docs/spec/22-uk-launch-readiness.md
 * §22.5). The screens at the redirect targets, and the Yoti
 * verification edge function, land in Wave 2 / v2.
 */

import { getFeatures, type Features } from '../features';

export type AgeGateRegion = 'gb' | 'us';

export type GbAgeGateStatus =
  | { region: 'gb'; status: 'UNDER_13_DEFERRED'; redirect: '/auth/under-13-deferred' }
  | { region: 'gb'; status: 'REQUIRES_PARENTAL_CONSENT'; redirect: '/auth/uk-gdpr/parental-consent' }
  | { region: 'gb'; status: 'INFORMED_PARENTAL_NOTICE'; redirect: '/auth/uk-gdpr/teen-onboarding' }
  | { region: 'gb'; status: 'OK' };

export type UsAgeGateStatus =
  | { region: 'us'; status: 'UNDER_13_DEFERRED'; redirect: '/auth/under-13-deferred' }
  | { region: 'us'; status: 'REQUIRES_VPC'; redirect: '/auth/coppa/parental-consent' }
  | { region: 'us'; status: 'COPPA_2_CAUTION'; redirect: '/auth/onboarding' }
  | { region: 'us'; status: 'OK' };

export type AgeGateStatus = GbAgeGateStatus | UsAgeGateStatus;

/**
 * Whole-years age between two dates. Conservative — returns the
 * smaller value at boundary days (someone whose birthday is "today"
 * is treated as their new age). dob in the future returns a
 * negative value; callers should validate dob ≤ refDate.
 */
export function ageInYears(dob: Date, refDate: Date = new Date()): number {
  let age = refDate.getFullYear() - dob.getFullYear();
  const monthDelta = refDate.getMonth() - dob.getMonth();
  const dayDelta = refDate.getDate() - dob.getDate();
  if (monthDelta < 0 || (monthDelta === 0 && dayDelta < 0)) age -= 1;
  return age;
}

/**
 * UK pathway. DPA 2018 §9: digital consent age = 13.
 * @param dob date of birth
 * @param refDate reference "now" (test seam)
 * @param features feature-flag snapshot (test seam — default reads live flags)
 */
export function checkUkGdprCompliance(
  dob: Date,
  refDate?: Date,
  features: Features = getFeatures(),
): GbAgeGateStatus {
  const age = ageInYears(dob, refDate);
  if (age < 13) {
    if (!features.under13PlayerAccounts) {
      return {
        region: 'gb',
        status: 'UNDER_13_DEFERRED',
        redirect: '/auth/under-13-deferred',
      };
    }
    return {
      region: 'gb',
      status: 'REQUIRES_PARENTAL_CONSENT',
      redirect: '/auth/uk-gdpr/parental-consent',
    };
  }
  if (age < 18) {
    return {
      region: 'gb',
      status: 'INFORMED_PARENTAL_NOTICE',
      redirect: '/auth/uk-gdpr/teen-onboarding',
    };
  }
  return { region: 'gb', status: 'OK' };
}

/**
 * US pathway. COPPA = 13; COPPA 2.0 (Senate 2026) extends caution to 17.
 * @param dob date of birth
 * @param refDate reference "now" (test seam)
 * @param features feature-flag snapshot (test seam — default reads live flags)
 */
export function checkCoppaCompliance(
  dob: Date,
  refDate?: Date,
  features: Features = getFeatures(),
): UsAgeGateStatus {
  const age = ageInYears(dob, refDate);
  if (age < 13) {
    if (!features.under13PlayerAccounts) {
      return {
        region: 'us',
        status: 'UNDER_13_DEFERRED',
        redirect: '/auth/under-13-deferred',
      };
    }
    return {
      region: 'us',
      status: 'REQUIRES_VPC',
      redirect: '/auth/coppa/parental-consent',
    };
  }
  if (age < 17) {
    return {
      region: 'us',
      status: 'COPPA_2_CAUTION',
      redirect: '/auth/onboarding',
    };
  }
  return { region: 'us', status: 'OK' };
}

/**
 * Region-aware entry point. Call from registration / onboarding screens.
 * @param features feature-flag snapshot (test seam — default reads live flags)
 */
export function checkAgeCompliance(
  dob: Date,
  region: AgeGateRegion,
  refDate?: Date,
  features?: Features,
): AgeGateStatus {
  return region === 'gb'
    ? checkUkGdprCompliance(dob, refDate, features)
    : checkCoppaCompliance(dob, refDate, features);
}

/**
 * Granular consent categories for the UK parental-consent flow.
 * Each category maps to one row in `consent_log` (one per ticked
 * box, never blanket — Children's Code Standard 7).
 */
export const UK_CONSENT_CATEGORIES = [
  'account_creation',
  'ratings',
  'wellness_logs',
  'bio_band',
  'video_sharing',
  'ai_development_analysis',
] as const;

export type UkConsentCategory = (typeof UK_CONSENT_CATEGORIES)[number];

/**
 * UX-facing labels for each category. Used by the parental-consent
 * flow Step 6 + (future) Settings → Privacy. Kept tight + parent-
 * readable; legal nuance lives in the privacy notice link below the
 * form.
 */
export const UK_CONSENT_CATEGORY_LABELS: Record<
  UkConsentCategory,
  { label: string; sub: string; required?: boolean }
> = {
  account_creation: {
    label: "Set up your child's 4CM account",
    sub: "The basics: name, email, club, team. Without this they can't use 4CM at all.",
    required: true,
  },
  ratings: {
    label: "Let your child's coach rate their progress",
    sub: 'Coach scores across the four corners (technical, physical, psychological, social). Your child sees scores after the coach posts them.',
  },
  wellness_logs: {
    label: 'Let your child log how they feel',
    sub: "Your child can record sleep, mood, soreness if they want. Helps the coach spot if they're having a tough week. Health data — child + coach only.",
  },
  bio_band: {
    label: "Track your child's growth (height, weight)",
    sub: 'Helps the coach plan training that fits how your child is growing. Never used to decide who makes a team — sport-science only.',
  },
  video_sharing: {
    label: 'Let your child appear in coaching clips',
    sub: 'Coach might save a moment from a game video. Your child sees any notes attached. You can revoke any time.',
  },
  ai_development_analysis: {
    label: "Let AI write a short note about your child's progress",
    sub: 'Off by default. AI looks at ratings + goals + wellness; writes a sentence or two for your child and the coach. Never decides team selection.',
  },
};

/**
 * UX-facing parental-consent category → consent_type enum mapping.
 * The enum lives in Postgres (migration 00079); this map keeps the
 * client-side category names stable while the DB stores canonical
 * enum values for filtering.
 */
export const PARENTAL_CONSENT_DB_TYPE: Record<UkConsentCategory, string> = {
  account_creation: 'ACCOUNT_CREATION',
  ratings: 'COACH_RATINGS',
  wellness_logs: 'WELLNESS',
  bio_band: 'BIO_BAND',
  video_sharing: 'MEDIA',
  ai_development_analysis: 'AI_DEVELOPMENT_ANALYSIS',
};

/**
 * Verification methods accepted by the ICO under UK-GDPR Art 8(2).
 * Health-data processing (wellness, bio-bands) RULES OUT text-only
 * — at least one of the stronger methods must be used.
 */
export const UK_VERIFICATION_METHODS = ['kba', 'id_face_match', 'text_confirm'] as const;
export type UkVerificationMethod = (typeof UK_VERIFICATION_METHODS)[number];

/**
 * Returns true when the chosen verification method is sufficient
 * given the categories the parent consented to. Text-only is
 * insufficient if any health-data category is consented to.
 */
export function isVerificationSufficient(
  method: UkVerificationMethod,
  categories: readonly UkConsentCategory[],
): boolean {
  const consentsToHealthData =
    categories.includes('wellness_logs') ||
    categories.includes('ratings'); // ratings can include health context
  if (consentsToHealthData && method === 'text_confirm') return false;
  return true;
}
